import { useMemo, useState, useEffect } from 'react';
import {
  GraduationCap, Search, X, Rocket, Boxes, HeartPulse, ShieldCheck, BookMarked,
  CreditCard, ArrowLeftRight, Gauge, Wrench,
  Info, Lightbulb, AlertTriangle, ShieldAlert, ChevronRight, ChevronDown, ArrowRight, ArrowLeft,
  CheckCircle2, Circle, Clock, ArrowUpRight, Sparkles,
} from 'lucide-react';
import { COURSE, GLOSSARY, ALL_LESSONS, searchCourse } from '../../data/accCourse';

const MODULE_ICONS = { Rocket, Boxes, CreditCard, HeartPulse, ArrowLeftRight, Gauge, Wrench, ShieldCheck, BookMarked };
const GLOSSARY_ID = '__glossary__';
const STORE_KEY = 'acc_academy_done';

const CALLOUT = {
  info: { icon: Info, cls: 'bg-blue-50 border-blue-200 text-blue-900', ic: 'text-blue-500' },
  tip: { icon: Lightbulb, cls: 'bg-emerald-50 border-emerald-200 text-emerald-900', ic: 'text-emerald-500' },
  warn: { icon: AlertTriangle, cls: 'bg-amber-50 border-amber-200 text-amber-900', ic: 'text-amber-500' },
  rule: { icon: ShieldAlert, cls: 'bg-violet-50 border-violet-200 text-violet-900', ic: 'text-violet-500' },
};

function Block({ b }) {
  switch (b.type) {
    case 'p': return <p className="text-[15px] text-gray-700 leading-relaxed">{b.text}</p>;
    case 'h': return <h4 className="text-base font-bold text-gray-900 pt-2">{b.text}</h4>;
    case 'analogy':
      return (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 p-4 flex gap-3">
          <Sparkles className="w-5 h-5 text-violet-500 flex-none mt-0.5" />
          <div><span className="text-xs font-bold uppercase tracking-wide text-violet-500">In plain terms</span>
            <p className="text-[15px] text-gray-700 mt-1 leading-relaxed">{b.text}</p></div>
        </div>
      );
    case 'callout': {
      const c = CALLOUT[b.tone] || CALLOUT.info; const Icon = c.icon;
      return (
        <div className={`rounded-xl border p-4 flex gap-3 ${c.cls}`}>
          <Icon className={`w-5 h-5 flex-none mt-0.5 ${c.ic}`} />
          <div>{b.title && <p className="font-semibold text-sm">{b.title}</p>}
            <p className="text-sm mt-0.5 leading-relaxed">{b.text}</p></div>
        </div>
      );
    }
    case 'list':
      return <ul className="space-y-1.5">{b.items.map((it, i) => (
        <li key={i} className="flex gap-2 text-[15px] text-gray-700"><ChevronRight className="w-4 h-4 text-gray-300 flex-none mt-1" />{it}</li>
      ))}</ul>;
    case 'steps':
      return <ol className="space-y-3">{b.items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex-none w-7 h-7 rounded-full bg-violet-100 text-violet-700 font-bold text-sm flex items-center justify-center">{i + 1}</span>
          <div><p className="font-semibold text-gray-900 text-[15px]">{it.title}</p><p className="text-sm text-gray-600 leading-relaxed">{it.text}</p></div>
        </li>
      ))}</ol>;
    case 'terms':
      return <dl className="rounded-xl border border-gray-200 divide-y divide-gray-100">{b.items.map((it, i) => (
        <div key={i} className="px-4 py-2.5"><dt className="font-semibold text-gray-900 text-sm">{it.term}</dt>
          <dd className="text-sm text-gray-600 mt-0.5 leading-relaxed">{it.def}</dd></div>
      ))}</dl>;
    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm"><thead className="bg-gray-50"><tr>
            {b.head.map((h, i) => <th key={i} className="text-left px-4 py-2 font-semibold text-gray-600">{h}</th>)}
          </tr></thead><tbody>{b.rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100">{r.map((c, j) => <td key={j} className="px-4 py-2 text-gray-700">{c}</td>)}</tr>
          ))}</tbody></table>
        </div>
      );
    case 'faq':
      return <div className="space-y-2">{b.items.map((it, i) => (
        <div key={i} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
          <p className="font-semibold text-gray-800 text-sm">{it.q}</p>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">{it.a}</p></div>
      ))}</div>;
    default: return null;
  }
}

function highlight(text, q) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-yellow-200 rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

export default function AccLearn({ onNavigate }) {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(ALL_LESSONS[0].id);
  const [openModules, setOpenModules] = useState(() => new Set(COURSE.map((m) => m.id)));
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORE_KEY) || '[]')); } catch { return new Set(); }
  });

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...done])); } catch { /* ignore */ }
  }, [done]);

  const results = useMemo(() => searchCourse(query), [query]);
  const searching = query.trim().length > 0;

  const activeLesson = activeId === GLOSSARY_ID ? null : ALL_LESSONS.find((l) => l.id === activeId);
  const idx = ALL_LESSONS.findIndex((l) => l.id === activeId);
  const prev = idx > 0 ? ALL_LESSONS[idx - 1] : null;
  const next = idx >= 0 && idx < ALL_LESSONS.length - 1 ? ALL_LESSONS[idx + 1] : null;

  const total = ALL_LESSONS.length;
  const completedCount = ALL_LESSONS.filter((l) => done.has(l.id)).length;
  const pct = Math.round((completedCount / total) * 100);

  function open(id) { setActiveId(id); setQuery(''); window.scrollTo?.({ top: 0, behavior: 'smooth' }); }
  function toggleModule(id) {
    setOpenModules((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function markDone(id) { setDone((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-violet-500" /> ACC Academy
          </h2>
          <p className="text-gray-500 mt-1 text-sm">Learn how FIS configuration works — in plain English, no technical background needed.</p>
        </div>
        <div className="min-w-[180px]">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Your progress</span><span className="font-semibold">{completedCount}/{total}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the guide — e.g. “purse”, “HAWK”, “expiry”, “MCC”…"
          className="w-full rounded-xl border border-gray-300 pl-10 pr-10 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400"
        />
        {searching && <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>}
      </div>

      {/* SEARCH RESULTS */}
      {searching ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{results.lessons.length + results.terms.length} result{(results.lessons.length + results.terms.length) === 1 ? '' : 's'} for “{query}”</p>
          {results.lessons.length === 0 && results.terms.length === 0 && (
            <div className="rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">No matches. Try a simpler word like “purse”, “card” or “fee”.</div>
          )}
          {results.lessons.map((l) => (
            <button key={l.id} onClick={() => open(l.id)} className="w-full text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-violet-300 hover:shadow-sm transition">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-500">{l.moduleTitle}</div>
              <div className="font-semibold text-gray-900 mt-0.5">{highlight(l.title, query)}</div>
              <p className="text-sm text-gray-500 mt-1">{highlight(l.summary, query)}</p>
            </button>
          ))}
          {results.terms.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="px-4 py-2.5 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5" /> Glossary matches</div>
              <dl className="divide-y divide-gray-100">{results.terms.map((g) => (
                <div key={g.term} className="px-4 py-2.5"><dt className="font-semibold text-gray-900 text-sm">{highlight(g.term, query)}</dt><dd className="text-sm text-gray-600 mt-0.5">{highlight(g.short, query)}</dd></div>
              ))}</dl>
              <button onClick={() => open(GLOSSARY_ID)} className="w-full text-left px-4 py-2.5 text-sm text-violet-600 hover:bg-violet-50 border-t border-gray-100">Open full glossary →</button>
            </div>
          )}
        </div>
      ) : (
        /* COURSE LAYOUT */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3 rounded-xl border border-gray-200 bg-white overflow-hidden lg:sticky lg:top-4">
            <div className="max-h-[70vh] overflow-auto py-1">
              {COURSE.map((m) => {
                const Icon = MODULE_ICONS[m.icon] || Boxes;
                const isOpen = openModules.has(m.id);
                return (
                  <div key={m.id}>
                    <button onClick={() => toggleModule(m.id)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                      <Icon className="w-4 h-4 text-violet-500" />
                      <span className="text-sm font-semibold text-gray-800">{m.title}</span>
                    </button>
                    {isOpen && m.lessons.map((l) => {
                      const on = l.id === activeId;
                      return (
                        <button key={l.id} onClick={() => open(l.id)} className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 text-left text-sm border-l-2 ${on ? 'bg-violet-50 border-violet-500 text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:bg-gray-50'}`}>
                          {done.has(l.id) ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-none" /> : <Circle className="w-3.5 h-3.5 text-gray-300 flex-none" />}
                          <span className="truncate">{l.title}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <button onClick={() => open(GLOSSARY_ID)} className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm border-t border-gray-100 ${activeId === GLOSSARY_ID ? 'bg-violet-50 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <BookMarked className="w-4 h-4 text-violet-500" /> Glossary
              </button>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-8 xl:col-span-9">
            {activeId === GLOSSARY_ID ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><BookMarked className="w-5 h-5 text-violet-500" /> Glossary</h3>
                  <p className="text-sm text-gray-500 mt-1">Quick definitions for the terms you’ll meet. Use the search box above to jump straight to one.</p>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {GLOSSARY.map((g) => (
                    <div key={g.term} className="rounded-xl border border-gray-200 p-3.5">
                      <dt className="font-semibold text-gray-900 text-sm">{g.term}</dt>
                      <dd className="text-sm text-gray-600 mt-1 leading-relaxed">{g.short}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : (
              <article className="rounded-2xl border border-gray-200 bg-white p-6">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-violet-500">
                  <span>{activeLesson.moduleTitle}</span>
                  <span className="text-gray-300">•</span>
                  <span className="inline-flex items-center gap-1 text-gray-400 normal-case font-normal"><Clock className="w-3 h-3" />{activeLesson.minutes} min read</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{activeLesson.title}</h3>
                <p className="text-gray-500 mt-1">{activeLesson.summary}</p>

                <div className="mt-5 space-y-4">
                  {activeLesson.blocks.map((b, i) => <Block key={i} b={b} />)}
                </div>

                {activeLesson.cta && (
                  <button onClick={() => onNavigate?.(activeLesson.cta.section)}
                    className="mt-6 inline-flex items-center gap-2 bg-violet-600 text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-violet-700">
                    {activeLesson.cta.label} <ArrowUpRight className="w-4 h-4" />
                  </button>
                )}

                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                  <button onClick={() => markDone(activeLesson.id)}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-lg px-3 py-2 border ${done.has(activeLesson.id) ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    {done.has(activeLesson.id) ? <><CheckCircle2 className="w-4 h-4" /> Completed</> : <><Circle className="w-4 h-4" /> Mark as read</>}
                  </button>
                  <div className="flex items-center gap-2">
                    {prev && <button onClick={() => open(prev.id)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-2 py-2"><ArrowLeft className="w-4 h-4" /> Back</button>}
                    {next && <button onClick={() => open(next.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-800 px-2 py-2">Next <ArrowRight className="w-4 h-4" /></button>}
                  </div>
                </div>
              </article>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
